import { ReviewStarInput } from 'components/atoms/ReviewStarInput'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import { postService, reservationService } from '../../core/services'
import * as constants from '../../static/constants'
import { Button, InputText, InputTextArea } from '../atoms'
import { Form } from '../molecules'
import SvgIcon from 'components/atoms/SvgIcon'

interface IProps {
  review: any
  reservation: any
  post: any
  post_images: any[]
  reviewer: any
  token?: string
  isDirectReservation?: boolean
}

const FIELDS = {
  rating: 'rating',
  rating_friendliness: 'rating_friendliness',
  rating_hospitality: 'rating_hospitality',
  rating_informative: 'rating_informative',
  rating_involved: 'rating_involved',
  rating_language: 'rating_language',
  rating_passion: 'rating_passion',
  body: 'body',
}

const formatScore = score => Number(score) * 2
const ReviewNew: React.FC<IProps> = props => {
  const { post } = postService.getPostFromJson(props.post)
  const { reservation } = reservationService.getReservationFromJson(props.reservation)
  const [errors, setErrors] = React.useState({ body: null })
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false)
  const [completed, setCompleted] = React.useState(false)
  const [images, setImages] = React.useState([])

  const handleUpdateForm = (updatedErrors, updatedIsSubmitEnabled) => {
    setErrors(updatedErrors)
    setIsSubmitEnabled(updatedIsSubmitEnabled)
  }

  const handleSubmit = React.useCallback(
    async (_, values) => {
      const base = {
        rating: formatScore(values.rating),
        rating_friendliness: formatScore(values.rating_friendliness),
        rating_hospitality: formatScore(values.rating_hospitality),
        rating_informative: formatScore(values.rating_informative),
        rating_involved: formatScore(values.rating_involved),
        rating_language: formatScore(values.rating_language),
        rating_passion: formatScore(values.rating_passion),
        body: values.body,
        reviewer_name: values.reviewer_name,
        token: values.token,
        reservation_type: props.isDirectReservation ? 'direct' : 'internal',
      }

      const params = props.isDirectReservation
        ? {
            direct_reservation_id: reservation.id,
            ...base,
          }
        : {
            reservation_id: reservation.id,
            ...base,
          }

      const formData = new FormData()
      Object.keys(params).forEach(key => {
        formData.append(`review[${key}]`, params[key])
      })
      images.forEach((image, index) => {
        Object.keys(image).forEach(key => {
          const name = `review[review_images_attributes][][${key}]`
          formData.append(name, image[key])
        })
      })

      if (props.token) {
        await postService.createReviewForTokenIssuedLink(formData)
      } else if (props.isDirectReservation) {
        await postService.createReviewForDirectReservation(formData)
      } else {
        await postService.createReview(formData)
      }

      setCompleted(true)
    },
    [images]
  )

  const onChangeImage = e => {
    const file = e.target.files[0]
    setImages([{ image: file }])
  }

  const isNormalReview = React.useMemo(() => {
    return !props.token && !props.isDirectReservation
  }, [props])

  const fields = React.useMemo(() => {
    return props.token ? { token: 'token', reviewer_name: 'reviewer_name', ...FIELDS } : FIELDS
  }, [props])

  return (
    <ReviewNewWrapper>
      <div className="ReviewNew_Main">
        <div className="ReviewNew_Post">
          <div className="ReviewNew_Image">
            <img src={post.post_images[0].image_url} alt="" />
          </div>
          <div className="ReviewNew_PostInfo">
            <h2 className="ReviewNew_PostTitle">{post.name}</h2>
            <div className="ReviewNew_PostOwner">
              <div className="ReviewNew_Avatar">
                <img src={post.user.avatar_url} alt="" />
              </div>
              <span>{post.user.username}</span>
            </div>
          </div>
        </div>
        <div className="ReviewNew_Editor">
          {completed ? (
            <div className="ReviewNew_Completed">
              <Done>
                <SvgIcon
                  name="check"
                  className="icon filled"
                  ariaHidden={true}
                  role="presentation"
                />
                <p>{I18n.t('review.completed')}</p>
              </Done>
              {isNormalReview && (
                <div className="ReviewNew_CompletedFooter">
                  <a href="/reservations?past=true">{I18n.t('review.back_to_index')}</a>
                </div>
              )}
            </div>
          ) : (
            <Form fields={fields} handleSubmit={handleSubmit} handleUpdateForm={handleUpdateForm}>
              <h2>{I18n.t('review.add_review')}</h2>
              <div>
                <ReviewStarInput label="Rating" name="rating" />
                <ReviewStarInput label="Friendliness" name="rating_friendliness" />
                <ReviewStarInput label="Hospitality" name="rating_hospitality" />
                <ReviewStarInput label="Informative" name="rating_informative" />
                <ReviewStarInput label="Involved" name="rating_involved" />
                <ReviewStarInput label="Language" name="rating_language" />
                <ReviewStarInput label="Passion" name="rating_passion" />
              </div>
              {props.token && (
                <>
                  <InputText
                    required={false}
                    name="reviewer_name"
                    label="Your Name"
                    defaultValue=""
                  />
                  <input type="hidden" name="token" value={props.token} />
                  <br />
                </>
              )}

              <div className="file-input">
                <input type="file" accept="image/png, image/jpeg" onChange={onChangeImage} />
              </div>

              <InputTextArea
                required={true}
                name="body"
                placeholder={I18n.t('placeholder.review_body')}
                defaultValue=""
                error={errors.body}
              />
              <p className="review-caption">
                All reviews are checked and confirmed before being posted.
              </p>
              <Button primary={true} disabled={!isSubmitEnabled}>
                {I18n.t('generic.send')}
              </Button>
            </Form>
          )}
        </div>
      </div>
    </ReviewNewWrapper>
  )
}

const Done = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
  padding: 8px;
  color: ${constants.COLORS.successText};
  border: solid 1px ${constants.COLORS.success};
  border-radius: 3px;
  box-shadow: 0 0 0 4px #dff7df inset;

  > p {
    margin-left: 12px;
    font-weight: bold;
    font-size: 14px;
  }

  .icon {
    color: ${constants.COLORS.success};
  }
`

const ReviewNewWrapper = styled.div`
  padding: 24px 12px 0;

  .ReviewNew_Editor {
    margin: 24px 0;
    border-top: solid 1px ${constants.COLORS.divider};

    h2 {
      margin-top: 12px;
      font-size: 18px;
    }

    .Button {
      margin-top: 12px;
    }
  }

  .ReviewNew_Main {
    max-width: 800px;
    margin: 0 auto;
  }

  .ReviewNew_Post {
    display: flex;
    align-items: center;
  }

  .ReviewNew_Image {
    width: 200px;
    height: 160px;
    border-radius: 3px;
    overflow: hidden;

    > img {
      width: 100%;
      height: inherit;
      object-fit: cover;
    }
  }

  .ReviewNew_PostInfo {
    flex: 1;
    margin-left: 16px;
  }

  .ReviewNew_PostTitle {
    font-size: 20px;
  }

  .ReviewNew_PostOwner {
    display: flex;
    align-items: center;

    > span {
      margin-left: 12px;
    }
  }

  .ReviewNew_Avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    margin-top: 12px;
    flex: none;

    > img {
      width: inherit;
      height: inherit;
      object-fit: cover;
    }
  }

  .ReviewNew_CompletedFooter {
    display: flex;
    align-items: center;
    justify-content: center;

    > a {
      color: ${constants.COLORS.primary};
      text-decoration: underline;
    }
  }
  .file-input {
    margin: 16px 0;
  }
  .review-caption {
    position: relative;
    line-height: 16px;
    margin-top: 12px;
    font-size: 13px;
    padding-left: 13px;
    &::before {
      content: '*';
      position: absolute;
      left: 0;
    }
  }
`

export default ReviewNew
